/**
 * "How many people would you like to feed?"
 *
 * The tile subtitles are the preset count multiplied by the *selected meal's*
 * price, recomputed whenever the meal changes. The artwork's tiles were drawn
 * for a Rs.160 meal; hardcoding those figures would misprice every Rs.180
 * donation, so the prices are derived here and confirmed by a server quote
 * before anything is charged.
 */

import { memo, useState } from 'react';
import { Pressable, StyleSheet, TextInput, View, type ViewStyle } from 'react-native';

import { formatMoney, previewTotal, type AppConfig, type Money } from '@mfa/domain';
import { colors, radius, space, textStyles } from '../../theme';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

/**
 * The browser drew its blue focus ring over the green border of the tile a
 * donor had just tapped, so a selected tile looked like it was in some other
 * state. Selection is already shown; the ring is dropped where it repeats it.
 */
const NO_OUTLINE = { outlineStyle: 'none' } as unknown as ViewStyle;

export interface QuantitySelectorProps {
  presets: number[];
  unitPrice: Money | null;
  value: number | null;
  onChange: (count: number | null) => void;
  rules: AppConfig['people'];
  /** Validation message from the shared domain validator. */
  error?: string | null;
  testID?: string;
}

export const QuantitySelector = memo(function QuantitySelector({
  presets,
  unitPrice,
  value,
  onChange,
  rules,
  error,
  testID = 'quantity-selector',
}: QuantitySelectorProps) {
  const t = useT();
  const isPreset = value !== null && presets.includes(value);
  const [customMode, setCustomMode] = useState(value !== null && !isPreset);
  const [customText, setCustomText] = useState(value && !isPreset ? String(value) : '');

  const selectPreset = (count: number) => {
    setCustomMode(false);
    setCustomText('');
    onChange(count);
  };

  const openCustom = () => {
    setCustomMode(true);
    onChange(null);
  };

  const handleCustomChange = (text: string) => {
    // Digits only: a decimal headcount is not a thing.
    const digits = text.replace(/[^0-9]/g, '');
    setCustomText(digits);
    onChange(digits ? Number(digits) : null);
  };

  return (
    <View testID={testID}>
      <View style={styles.tiles} accessibilityRole="radiogroup">
        {presets.map((count) => {
          const selected = !customMode && value === count;
          const total = unitPrice ? previewTotal(unitPrice, count) : null;
          return (
            <Pressable
              key={count}
              testID={`quantity-${count}`}
              onPress={() => selectPreset(count)}
              accessibilityRole="radio"
              accessibilityState={{ selected }}
              accessibilityLabel={`${count} people${total ? `, ${formatMoney(total)}` : ''}`}
              style={[styles.tile, selected && styles.tileSelected, selected && NO_OUTLINE]}
            >
              <Text variant="cardTitle" align="center">
                {count} {t('People')}
              </Text>
              <Text variant="caption" tone="secondary" align="center">
                {total ? formatMoney(total) : '—'}
              </Text>
            </Pressable>
          );
        })}

        <Pressable
          testID="quantity-custom"
          onPress={openCustom}
          accessibilityRole="radio"
          accessibilityState={{ selected: customMode }}
          accessibilityLabel={t('Custom number of people')}
          style={[styles.tile, customMode && styles.tileSelected, customMode && NO_OUTLINE]}
        >
          <Text variant="cardTitle" align="center">{t('Custom')}</Text>
          <Text variant="caption" tone="secondary" align="center">{t('Enter Number')}</Text>
        </Pressable>
      </View>

      {customMode ? (
        <View style={styles.customPanel} testID="custom-panel">
          <Text variant="label" tone="secondary">{t('How many people?')}</Text>
          <TextInput
            testID="custom-people-input"
            value={customText}
            onChangeText={handleCustomChange}
            keyboardType="number-pad"
            inputMode="numeric"
            placeholder={`${rules.min} – ${rules.max}`}
            placeholderTextColor={colors.text.disabled}
            style={[styles.input, error ? styles.inputError : null]}
            accessibilityLabel={t('Number of people to feed')}
            maxLength={String(rules.max).length}
          />
          {value && unitPrice ? (
            <Text variant="caption" tone="brand">
              Total {formatMoney(previewTotal(unitPrice, value))}
            </Text>
          ) : null}
        </View>
      ) : null}

      {error ? (
        <Text variant="caption" tone="danger" style={styles.error} accessibilityLiveRegion="polite">
          {error}
        </Text>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  tiles: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.sm,
  },
  tile: {
    flexGrow: 1,
    flexBasis: '30%',
    minWidth: 96,
    paddingVertical: space.md,
    paddingHorizontal: space.sm,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border.default,
    backgroundColor: colors.bg.surface,
    gap: space.xxs,
  },
  tileSelected: {
    borderColor: colors.border.brand,
    borderWidth: 2,
    backgroundColor: colors.bg.selected,
  },
  customPanel: {
    marginTop: space.md,
    gap: space.xs,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border.default,
    borderRadius: radius.md,
    paddingHorizontal: space.md,
    paddingVertical: space.md,
    backgroundColor: colors.bg.surface,
    color: colors.text.primary,
    fontSize: textStyles.body.fontSize,
  },
  inputError: {
    borderColor: colors.text.danger,
  },
  error: {
    marginTop: space.sm,
  },
});
